import React, { useState, useEffect, useCallback } from 'react';
import InvoiceTable from '../../components/InvoiceTable';
import InvoiceForm from '../../components/InvoiceForm';
import { FileText, PlusCircle, LayoutGrid, Clock, CheckCircle2, AlertCircle, Trash2, X, Inbox } from 'lucide-react';
import { billingApi, Invoice, BackendInvoiceStatus } from '../../api/billingApi';
import { getBranding, BrandingData } from '../../api/brandingApi';
import InvoicePreview from '../../components/InvoicePreview';
import { toast } from 'react-toastify';

type TabType = 'All' | 'Create' | 'Drafts' | 'Sent' | 'Paid' | 'Overdue';

export default function InvoicesModule() {
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [branding, setBranding] = useState<BrandingData | null>(null);
  const [loading, setLoading] = useState(true);
  const [editingInvoice, setEditingInvoice] = useState<Invoice | null>(null);
  const [previewInvoiceId, setPreviewInvoiceId] = useState<string | null>(null);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [invoiceToDelete, setInvoiceToDelete] = useState<Invoice | null>(null);
  const [activeTab, setActiveTab] = useState<TabType>('All');

  const getFilteredInvoices = () => {
    if (activeTab === 'All') return invoices;
    const statusMap: Record<string, BackendInvoiceStatus> = {
      'Drafts': BackendInvoiceStatus.Draft,
      'Sent': BackendInvoiceStatus.Sent,
      'Paid': BackendInvoiceStatus.Paid,
      'Overdue': BackendInvoiceStatus.Overdue
    };
    return invoices.filter(inv => inv.status === statusMap[activeTab]);
  };

  const tabs = [
    { id: 'All', label: 'All Invoices', icon: <LayoutGrid size={18} /> },
    { id: 'Create', label: 'Create Invoice', icon: <PlusCircle size={18} /> },
    { id: 'Drafts', label: 'Drafts', icon: <Clock size={18} /> },
    { id: 'Sent', label: 'Sent', icon: <CheckCircle2 size={18} /> },
    { id: 'Paid', label: 'Paid', icon: <CheckCircle2 size={18} /> },
    { id: 'Overdue', label: 'Overdue', icon: <AlertCircle size={18} /> },
  ];

  const fetchInvoices = useCallback(async () => {
    setLoading(true);
    try {
      const fetchedInvoices = await billingApi.getInvoices();
      setInvoices(fetchedInvoices);
    } catch (error) {
      // Error already handled by billingApi.ts toast
      setInvoices([]); // Clear invoices on error
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchInvoices();
    
    const loadBranding = async () => {
      const data = await getBranding();
      setBranding(data);
    };
    loadBranding();
  }, [fetchInvoices]);

  const handleInvoiceCreated = (newInvoiceId: string) => {
    setEditingInvoice(null);
    fetchInvoices(); // Refresh the list
    setActiveTab('All'); // Switch to All Invoices tab
  };

  const handleEdit = (invoice: Invoice) => {
    setEditingInvoice(invoice);
    setActiveTab('Create');
  };

  const handleDelete = async (id: string) => {
    const invoice = invoices.find(inv => inv.id === id);
    if (invoice) {
      setInvoiceToDelete(invoice);
      setDeleteModalOpen(true);
    }
  };

  const confirmDelete = async () => {
    if (!invoiceToDelete) return;
    try {
      await billingApi.deleteInvoice(invoiceToDelete.id);
      toast.success(`Invoice ${invoiceToDelete.invoiceNumber} deleted successfully!`);
      setDeleteModalOpen(false);
      fetchInvoices();
    } catch {
      // Error is already toasted by the billingApi wrapper
    }
  };

  const handleSend = async (id: string) => {
    try {
      await billingApi.sendInvoice(id);
      toast.success("Invoice sent successfully");
      fetchInvoices();
    } catch {
      // Error is already toasted by the billingApi wrapper
    }
  };

  const handleDownload = async (id: string, fileName: string) => {
    try {
      const blob = await billingApi.downloadPdf(id);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${fileName || 'invoice'}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch {
      // Error is already toasted by the billingApi wrapper
    }
  };

  const handleView = (id: string) => {
    setPreviewInvoiceId(id);
  };

  const onTabChange = (tab: TabType) => {
    if (tab !== 'Create') setEditingInvoice(null);
    setActiveTab(tab);
  };

  return (
    <>
      <div className="min-h-screen pt-10 p-6 md:p-8 lg:p-10 md:ml-0">
        {/* Header */}
        <header className="mt-5 mb-8 flex flex-col md:flex-row md:items-center justify-between gap-6">
          <div>
            <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-2 leading-tight mb-0">
              <FileText className="text-indigo-600" size={24} />
              Invoices
            </h1>
            <p className="text-slate-500 text-sm mt-1 text-left">
              Manage your client billing, proposals and payment collections.
            </p>
          </div>
        </header>

        {/* Tab Navigation */}
        <div className="w-full lg:w-auto overflow-visible">
          <div className="flex flex-wrap gap-1 p-1 md:p-1.5 bg-slate-100 rounded-2xl md:rounded-[1.25rem] border border-slate-200 shadow-inner mb-6">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => onTabChange(tab.id as TabType)}
                className={`flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl text-xs md:text-sm font-bold transition-all flex-1 lg:flex-none ${
                  activeTab === tab.id
                    ? 'bg-white text-indigo-600 shadow-sm ring-1 ring-slate-200'
                    : 'text-slate-500 hover:text-slate-900 active:bg-slate-200/50'
                }`}
              >
                {tab.icon}
                {tab.label}
              </button>
            ))}
          </div>
        </div>
      {/* Tab Content */}
        <div className="transition-all duration-300">
        {activeTab === 'Create' ? (
          <InvoiceForm 
            onInvoiceCreated={handleInvoiceCreated} 
            initialData={editingInvoice}
          />
        ) : (
          loading ? (
            <div className="bg-white rounded-[2rem] border border-slate-200 p-20 flex justify-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
            </div>
          ) : getFilteredInvoices().length > 0 ? (
            <InvoiceTable 
              invoices={getFilteredInvoices()} 
              loading={loading} 
              filterStatus={activeTab === 'All' ? 'All' : (
                activeTab === 'Drafts' ? BackendInvoiceStatus.Draft :
                activeTab === 'Sent' ? BackendInvoiceStatus.Sent :
                activeTab === 'Paid' ? BackendInvoiceStatus.Paid : BackendInvoiceStatus.Overdue
              )}
              onEdit={handleEdit} 
              onDelete={handleDelete}
              onSend={handleSend}
              onDownload={handleDownload}
              onView={handleView}
            />
          ) : (
            <div className="bg-white rounded-[2rem] border border-slate-200 p-20 text-center animate-fade-in">
              <Inbox className="mx-auto text-slate-200 mb-4" size={48} />
              <p className="text-slate-400 font-bold uppercase tracking-widest text-sm">No invoices found</p>
            </div>
          )
        )}
        </div>

      {/* Quick Stats / Info Footer */}
        {activeTab === 'All' && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-8">
            <div className="bg-white p-6 rounded-[2rem] border border-slate-100 shadow-sm flex items-center gap-4">
              <div className="w-12 h-12 shrink-0 bg-blue-50 rounded-2xl flex items-center justify-center text-blue-600 font-bold">{invoices.length}</div>
              <div className="text-left"><p className="text-xs font-bold text-slate-400 uppercase tracking-widest">Total Invoices</p><p className="text-xl font-bold text-slate-900">${invoices.reduce((sum, inv) => sum + inv.totalAmount, 0).toLocaleString()}</p></div>
            </div>
            <div className="bg-white p-6 rounded-[2rem] border border-slate-100 shadow-sm flex items-center gap-4">
              <div className="w-12 h-12 shrink-0 bg-emerald-50 rounded-2xl flex items-center justify-center text-emerald-600 font-bold">{invoices.filter(inv => inv.status === BackendInvoiceStatus.Paid).length}</div>
              <div className="text-left"><p className="text-xs font-bold text-slate-400 uppercase tracking-widest">Paid</p><p className="text-xl font-bold text-slate-900">${invoices.filter(inv => inv.status === BackendInvoiceStatus.Paid).reduce((sum, inv) => sum + inv.totalAmount, 0).toLocaleString()}</p></div>
            </div>
            <div className="bg-white p-6 rounded-[2rem] border border-slate-100 shadow-sm flex items-center gap-4">
              <div className="w-12 h-12 shrink-0 bg-red-50 rounded-2xl flex items-center justify-center text-red-600 font-bold">{invoices.filter(inv => inv.status === BackendInvoiceStatus.Overdue).length}</div>
              <div className="text-left"><p className="text-xs font-bold text-slate-400 uppercase tracking-widest">Overdue</p><p className="text-xl font-bold text-slate-900">${invoices.filter(inv => inv.status === BackendInvoiceStatus.Overdue).reduce((sum, inv) => sum + inv.totalAmount, 0).toLocaleString()}</p></div>
            </div>
          </div>
        )}
      </div>

      <InvoicePreview 
        isOpen={!!previewInvoiceId} 
        onClose={() => setPreviewInvoiceId(null)} 
        invoiceId={previewInvoiceId || undefined}
        branding={branding}
        data={{
          clientName: '',
          clientEmail: '',
          customerAddress: '',
          customerPhone: '',
          dueDate: '',
          notes: '',
          items: [],
          totals: { subtotal: 0, tax: 0, total: 0 }
        }}
      />

      {/* Delete Confirmation Modal */}
      {deleteModalOpen && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm animate-fade-in" onClick={() => setDeleteModalOpen(false)} />
          <div className="relative w-full max-w-md bg-white rounded-[2.5rem] shadow-2xl p-8 animate-scale-in text-center overflow-hidden">
            <button onClick={() => setDeleteModalOpen(false)} className="absolute top-6 right-6 text-slate-400 hover:text-slate-600 transition-colors">
              <X size={24} />
            </button>

            <div className="w-20 h-20 bg-red-50 rounded-3xl flex items-center justify-center text-red-600 mx-auto mb-6">
              <Trash2 size={40} />
            </div>
            
            <h3 className="text-2xl font-bold text-slate-900 mb-2">Delete Invoice</h3>
            <p className="text-slate-500 font-medium mb-8 leading-relaxed px-4">
              Are you sure you want to delete <span className="font-medium text-slate-900">{invoiceToDelete?.invoiceNumber}</span>? 
              This action is permanent and cannot be undone.
            </p>

            <div className="flex gap-3">
              <button 
                onClick={() => setDeleteModalOpen(false)}
                className="flex-1 py-4 bg-slate-100 text-slate-600 rounded-2xl font-bold hover:bg-slate-200 transition-all active:scale-95"
              >
                Cancel
              </button>
              <button 
                onClick={confirmDelete}
                className="flex-1 py-4 bg-red-600 text-white rounded-2xl font-bold shadow-lg shadow-red-200 hover:bg-red-700 transition-all active:scale-95"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}